// License Error Handler Utility
// Handles all license-related error responses from API endpoints

export class LicenseError extends Error {
    constructor(errorData) {
        super(errorData.error || 'License error occurred');
        this.name = 'LicenseError';
        this.code = errorData.code;
        this.quotaType = errorData.quotaType;
        this.quota = errorData.quota;
        this.plan = errorData.plan;
        this.upgradeUrl = errorData.upgradeUrl;
        this.help = errorData.help;
        this.title = errorData.title;
        this.details = errorData.details;
        this.action = errorData.action;
        this.actionUrl = errorData.actionUrl;
        this.severity = errorData.severity;
        this.timestamp = errorData.timestamp;
        this.feature = errorData.feature;
        this.requiredPlan = errorData.requiredPlan;
        this.currentPlan = errorData.currentPlan;
        this._quotas = errorData._quotas;
        this._license = errorData._license;
        this.retryAfter = errorData.retryAfter;
        this.rawData = errorData;
    }
}

export const LICENSE_ERROR_CODES = {
    QUOTA_EXCEEDED: 'QUOTA_EXCEEDED',
    FEATURE_NOT_AVAILABLE: 'FEATURE_NOT_AVAILABLE',
    LICENSE_EXPIRED: 'LICENSE_EXPIRED',
    LICENSE_INVALID: 'LICENSE_INVALID',
    LICENSE_SUSPENDED: 'LICENSE_SUSPENDED',
    SETUP_REQUIRED: 'SETUP_REQUIRED',
    AUTH_REQUIRED: 'AUTH_REQUIRED',
    INSUFFICIENT_PERMISSIONS: 'INSUFFICIENT_PERMISSIONS',
    QUOTA_WARNING: 'QUOTA_WARNING',
    RATE_LIMIT_EXCEEDED: 'RATE_LIMIT_EXCEEDED',
    MAINTENANCE_MODE: 'MAINTENANCE_MODE'
};

export const QUOTA_TYPES = {
    USERS: 'users',
    CAMPAIGNS: 'campaigns',
    API_CALLS: 'api_calls',
    MESSAGES: 'messages',
    STORAGE: 'storage'
};

export const SEVERITY_LEVELS = {
    ERROR: 'error',
    WARNING: 'warning',
    INFO: 'info'
};

export const BLOCKING_LEVELS = {
    NO_BLOCKING: 0,
    WARNING_ONLY: 1,
    LIMITED_ACCESS: 2,
    BLOCKED: 3
};

// Check if an error response is a license-related error
export const isLicenseError = (error) => {
    if (!error) return false;
    
    // Check if it's already a LicenseError instance
    if (error instanceof LicenseError) return true;
    
    // Check if the error data contains license error indicators
    const errorData = typeof error === 'object' ? error : {};
    
    // Exclude successful responses that contain license info (like login responses)
    // These should have user/token data along with license info
    if (errorData.user || errorData.token) {
        return false;
    }
    
    // Only treat as license error if it has specific license error indicators
    return !!(
        (errorData.code && Object.values(LICENSE_ERROR_CODES).includes(errorData.code)) ||
        (errorData.error && errorData.quotaType) || // Error message + quota info
        (errorData.error && errorData.upgradeUrl) || // Error message + upgrade URL
        (errorData.severity && errorData.code) // Severity + error code
    );
};

// Parse API error response and create LicenseError if applicable
export const parseLicenseError = (errorResponse) => {
    if (!errorResponse) return null;
    
    let errorData;
    
    if (typeof errorResponse === 'string') {
        try {
            errorData = JSON.parse(errorResponse);
        } catch (e) {
            return null;
        }
    } else {
        errorData = errorResponse;
    }
    
    if (isLicenseError(errorData)) {
        return new LicenseError(errorData);
    }
    
    return null;
};

// Format quota information for display
export const formatQuotaInfo = (quota) => {
    if (!quota) return null;
    
    return {
        used: quota.used || 0,
        limit: quota.limit || 0,
        available: quota.available || 0,
        percentage: quota.percentage || 0,
        requested: quota.requested || 0,
        isExceeded: quota.percentage >= 100,
        isWarning: quota.percentage >= 80,
        displayText: `${quota.used}/${quota.limit} (${quota.percentage}%)`,
        progressPercentage: Math.min(quota.percentage, 100)
    };
};

// Get user-friendly messages for quota types
export const getQuotaTypeInfo = (quotaType) => {
    const quotaInfo = {
        [QUOTA_TYPES.USERS]: {
            name: 'Users',
            description: 'Team members with access to the platform',
            icon: '👥',
            unit: 'users'
        },
        [QUOTA_TYPES.CAMPAIGNS]: {
            name: 'Campaigns',
            description: 'Active marketing campaigns',
            icon: '📢',
            unit: 'campaigns'
        },
        [QUOTA_TYPES.API_CALLS]: {
            name: 'API Calls',
            description: 'Daily API requests',
            icon: '🔗',
            unit: 'calls'
        },
        [QUOTA_TYPES.MESSAGES]: {
            name: 'Messages',
            description: 'Daily WhatsApp messages',
            icon: '💬',
            unit: 'messages'
        },
        [QUOTA_TYPES.STORAGE]: {
            name: 'Storage',
            description: 'File storage space',
            icon: '💾',
            unit: 'MB'
        }
    };
    
    return quotaInfo[quotaType] || {
        name: quotaType,
        description: `${quotaType} quota`,
        icon: '📊',
        unit: 'units'
    };
};

// Get severity styling information
export const getSeverityStyle = (severity) => {
    const styles = {
        [SEVERITY_LEVELS.ERROR]: {
            bgColor: 'bg-red-50',
            borderColor: 'border-red-200',
            textColor: 'text-red-800',
            iconColor: 'text-red-600',
            buttonColor: 'bg-red-600 hover:bg-red-700'
        },
        [SEVERITY_LEVELS.WARNING]: {
            bgColor: 'bg-yellow-50',
            borderColor: 'border-yellow-200',
            textColor: 'text-yellow-800',
            iconColor: 'text-yellow-600',
            buttonColor: 'bg-yellow-600 hover:bg-yellow-700'
        },
        [SEVERITY_LEVELS.INFO]: {
            bgColor: 'bg-blue-50',
            borderColor: 'border-blue-200',
            textColor: 'text-blue-800',
            iconColor: 'text-blue-600',
            buttonColor: 'bg-blue-600 hover:bg-blue-700'
        }
    };
    
    return styles[severity] || styles[SEVERITY_LEVELS.INFO];
};

// Get blocking level information
export const getBlockingLevelInfo = (level) => {
    const info = {
        [BLOCKING_LEVELS.NO_BLOCKING]: {
            text: 'No restrictions',
            color: 'text-green-600',
            severity: SEVERITY_LEVELS.INFO
        },
        [BLOCKING_LEVELS.WARNING_ONLY]: {
            text: 'Warning level',
            color: 'text-yellow-600',
            severity: SEVERITY_LEVELS.WARNING
        },
        [BLOCKING_LEVELS.LIMITED_ACCESS]: {
            text: 'Limited access',
            color: 'text-orange-600',
            severity: SEVERITY_LEVELS.WARNING
        },
        [BLOCKING_LEVELS.BLOCKED]: {
            text: 'Severely restricted',
            color: 'text-red-600',
            severity: SEVERITY_LEVELS.ERROR
        }
    };
    
    return info[level] || info[BLOCKING_LEVELS.NO_BLOCKING];
};

// Main error handler function that components can use
export const handleLicenseError = (error, options = {}) => {
    const {
        onQuotaExceeded,
        onFeatureNotAvailable,
        onLicenseExpired,
        onLicenseInvalid,
        onLicenseSuspended,
        onSetupRequired,
        onAuthRequired,
        onInsufficientPermissions,
        onRateLimitExceeded,
        onMaintenanceMode,
        onGenericError,
        // showToast = true, // Removed as it's not used in this function
        redirectOnAuth = true
    } = options;
    
    const licenseError = parseLicenseError(error);
    
    if (!licenseError) {
        if (onGenericError) {
            onGenericError(error);
        }
        return false; // Not a license error
    }
    
    // Handle specific error codes
    switch (licenseError.code) {
        case LICENSE_ERROR_CODES.QUOTA_EXCEEDED:
            if (onQuotaExceeded) {
                onQuotaExceeded(licenseError);
            }
            break;
            
        case LICENSE_ERROR_CODES.FEATURE_NOT_AVAILABLE:
            if (onFeatureNotAvailable) {
                onFeatureNotAvailable(licenseError);
            }
            break;
            
        case LICENSE_ERROR_CODES.LICENSE_EXPIRED:
            if (onLicenseExpired) {
                onLicenseExpired(licenseError);
            } else if (redirectOnAuth && licenseError.actionUrl) {
                window.location.href = licenseError.actionUrl;
            }
            break;
            
        case LICENSE_ERROR_CODES.LICENSE_INVALID:
            if (onLicenseInvalid) {
                onLicenseInvalid(licenseError);
            } else if (redirectOnAuth && licenseError.actionUrl) {
                window.location.href = licenseError.actionUrl;
            }
            break;
            
        case LICENSE_ERROR_CODES.LICENSE_SUSPENDED:
            if (onLicenseSuspended) {
                onLicenseSuspended(licenseError);
            }
            break;
            
        case LICENSE_ERROR_CODES.SETUP_REQUIRED:
            if (onSetupRequired) {
                onSetupRequired(licenseError);
            } else if (redirectOnAuth && licenseError.actionUrl) {
                window.location.href = licenseError.actionUrl;
            }
            break;
            
        case LICENSE_ERROR_CODES.AUTH_REQUIRED:
            if (onAuthRequired) {
                onAuthRequired(licenseError);
            } else if (redirectOnAuth) {
                // Clear token and redirect to login
                localStorage.removeItem('token');
                window.location.href = licenseError.actionUrl || '/login';
            }
            break;
            
        case LICENSE_ERROR_CODES.INSUFFICIENT_PERMISSIONS:
            if (onInsufficientPermissions) {
                onInsufficientPermissions(licenseError);
            }
            break;
            
        case LICENSE_ERROR_CODES.RATE_LIMIT_EXCEEDED:
            if (onRateLimitExceeded) {
                onRateLimitExceeded(licenseError);
            }
            break;
            
        case LICENSE_ERROR_CODES.MAINTENANCE_MODE:
            if (onMaintenanceMode) {
                onMaintenanceMode(licenseError);
            }
            break;
            
        default:
            if (onGenericError) {
                onGenericError(licenseError);
            }
            break;
    }
    
    return true; // Was a license error
};

const licenseErrorHandler = {
    LicenseError,
    LICENSE_ERROR_CODES,
    QUOTA_TYPES,
    SEVERITY_LEVELS,
    BLOCKING_LEVELS,
    isLicenseError,
    parseLicenseError,
    formatQuotaInfo,
    getQuotaTypeInfo,
    getSeverityStyle,
    getBlockingLevelInfo,
    handleLicenseError
};

export default licenseErrorHandler;